// Section 7: Exam Practice — Mixed Tests & Practice Paper-Style Questions
// Workbook-aligned practice from pages 160-189, with answer support from pages 200-204.
// Improvements:
// - Added marks for assessment weighting.
// - Added acceptedAnswers for common equivalent formatting.
// - Changed written definition questions from type: "number" to type: "text".
// - Corrected ambiguous geometry wording.
// - Added clearer hints and exam-style wording.
// - Preserved existing structure: learn -> practice -> level1/level2/level3.

export const examPracticeTopics = [
  {
    id: "7.1",
    title: "Mixed Practice Test 1",
    slug: "mixed-test-1",
    pageRef: "Pages 160-161",
    difficulty: "foundation",
    learn: {
      explanation:
        "Test your knowledge across the main GCSE maths topics with 10 quick mixed questions. These questions check key definitions, facts, methods and short calculations.",
      keyPoints: [
        "Covers: prime numbers, metric conversions, angle types, coordinates, sequences, data, perimeter, inequalities, averages and rearranging formulae.",
        "Use exact mathematical language where a written answer is needed.",
        "For algebra questions, show the inverse operation you used."
      ],
    },
    practice: {
      level1: [
        {
          id: "7.1.1",
          type: "choice",
          marks: 1,
          question: "What is a prime number?",
          options: [
            "A number with exactly two factors: 1 and itself",
            "An even number",
            "A number greater than 10",
            "A negative number"
          ],
          answer: "A number with exactly two factors: 1 and itself",
          hint1: "A prime number can only be divided exactly by 1 and itself.",
          hint2: "It has exactly two factors.",
          hint3: "A prime number has exactly two factors: 1 and itself."
        },
        {
          id: "7.1.2",
          type: "number",
          marks: 1,
          question: "How many millilitres are in 20 litres?",
          answer: "20000",
          acceptedAnswers: ["20000", "20,000", "20 000"],
          hint1: "1 litre = 1000 ml.",
          hint2: "20 × 1000 = 20 000.",
          hint3: "20 litres = 20 000 ml."
        },
        {
          id: "7.1.3",
          type: "choice",
          marks: 1,
          question: "A reflex angle is...",
          options: [
            "...less than 90°.",
            "...exactly 90°.",
            "...between 90° and 180°.",
            "...between 180° and 360°."
          ],
          answer: "...between 180° and 360°.",
          hint1: "A reflex angle is larger than a straight angle.",
          hint2: "A straight angle is 180°.",
          hint3: "A reflex angle is between 180° and 360°."
        },
        {
          id: "7.1.4",
          type: "text",
          marks: 1,
          question: "Give the coordinates of the point where the x-axis and y-axis cross. Write your answer as (x, y).",
          answer: "(0, 0)",
          acceptedAnswers: ["(0,0)", "(0, 0)", "0,0", "0, 0"],
          hint1: "The point where the axes cross is called the origin.",
          hint2: "At the origin, both coordinates are zero.",
          hint3: "The coordinates are (0, 0)."
        },
        {
          id: "7.1.5",
          type: "choice",
          marks: 1,
          question: "What is the next number in this sequence: 1, 7, 13, 19, ...?",
          options: ["13", "22", "25", "29"],
          answer: "25",
          hint1: "Find the difference between consecutive terms.",
          hint2: "The sequence increases by 6 each time.",
          hint3: "19 + 6 = 25."
        }
      ],
      level2: [
        {
          id: "7.1.6",
          type: "text",
          marks: 1,
          question: "What is discrete data?",
          answer: "Data that can only take certain exact values",
          acceptedAnswers: [
            "Data that can only take certain exact values",
            "Data that can only take specific values",
            "Data that can only take separate values",
            "Data that is counted rather than measured",
            "Data with fixed separate values"
          ],
          hint1: "Discrete data is not measured on a continuous scale.",
          hint2: "It usually comes from counting.",
          hint3: "Discrete data can only take certain exact values."
        },
        {
          id: "7.1.7",
          type: "text",
          marks: 1,
          question: "What is the perimeter of a shape?",
          answer: "The total distance around the edge of a shape",
          acceptedAnswers: [
            "The total distance around the edge of a shape",
            "The distance around the outside of a shape",
            "The total length around a shape",
            "The sum of all the side lengths"
          ],
          hint1: "Perimeter is found by adding side lengths.",
          hint2: "It measures the outside edge.",
          hint3: "The perimeter is the total distance around the edge of a shape."
        },
        {
          id: "7.1.8",
          type: "choice",
          marks: 1,
          question: "The statement x < 2 means...",
          options: [
            "...x is less than 2.",
            "...x is less than or equal to 2.",
            "...x is greater than 2.",
            "...x is greater than or equal to 2."
          ],
          answer: "...x is less than 2.",
          hint1: "< means less than.",
          hint2: "There is no line under the symbol, so 2 is not included.",
          hint3: "x < 2 means x is less than 2."
        },
        {
          id: "7.1.9",
          type: "number",
          marks: 1,
          question: "What is the median of these numbers: 1, 4, 5, 8, 12?",
          answer: "5",
          acceptedAnswers: ["5"],
          hint1: "The numbers are already in order.",
          hint2: "There are 5 values, so the median is the 3rd value.",
          hint3: "The median is 5."
        },
        {
          id: "7.1.10",
          type: "text",
          marks: 2,
          question: "Rearrange this equation to make x the subject: y = 2x − 6",
          answer: "x = (y + 6) / 2",
          acceptedAnswers: [
            "x = (y + 6) / 2",
            "x=(y+6)/2",
            "x = (y+6)/2",
            "x=(y + 6)/2",
            "(y + 6) / 2",
            "(y+6)/2",
            "y/2 + 3",
            "x = y/2 + 3",
            "x=y/2+3"
          ],
          hint1: "Add 6 to both sides first.",
          hint2: "y + 6 = 2x.",
          hint3: "Divide by 2: x = (y + 6) / 2."
        }
      ]
    }
  },

  {
    id: "7.2",
    title: "Mixed Practice Test 2",
    slug: "mixed-test-2",
    pageRef: "Pages 161-162",
    difficulty: "foundation",
    learn: {
      explanation:
        "This test checks more core GCSE skills, including graph facts, averages, number properties, ratio, sectors, estimation and simplifying algebra.",
      keyPoints: [
        "Covers: y-intercept, mean, Pythagoras, HCF, significant figures, primary data, ratios, sectors, estimating and simplifying expressions.",
        "For formula questions, remember the exact structure of the formula.",
        "For simplifying algebra, collect like terms carefully."
      ],
    },
    practice: {
      level1: [
        {
          id: "7.2.1",
          type: "text",
          marks: 1,
          question: "The equation of a straight line is y = 4x + 5. What are the coordinates of the y-intercept?",
          answer: "(0, 5)",
          acceptedAnswers: ["(0,5)", "(0, 5)", "0,5", "0, 5"],
          hint1: "In y = mx + c, the y-intercept is c.",
          hint2: "At the y-intercept, x = 0.",
          hint3: "The y-intercept is (0, 5)."
        },
        {
          id: "7.2.2",
          type: "choice",
          marks: 1,
          question: "The mean of a set of data is...",
          options: [
            "...the middle value.",
            "...the most common value.",
            "...the sum of all the values, divided by the number of values.",
            "...the difference between the largest and smallest value."
          ],
          answer: "...the sum of all the values, divided by the number of values.",
          hint1: "Add all the values first.",
          hint2: "Then divide by how many values there are.",
          hint3: "Mean = total ÷ number of values."
        },
        {
          id: "7.2.3",
          type: "choice",
          marks: 1,
          question: "What is the formula for Pythagoras' theorem?",
          options: ["y = mx + c", "a² + b² = c²", "sin x = opposite ÷ hypotenuse", "a + b + c + d = 360°"],
          answer: "a² + b² = c²",
          hint1: "Pythagoras is used with right-angled triangles.",
          hint2: "The two shorter sides squared add to the hypotenuse squared.",
          hint3: "a² + b² = c²."
        },
        {
          id: "7.2.4",
          type: "text",
          marks: 1,
          question: "What is the highest common factor of two numbers?",
          answer: "The biggest number that will divide into both numbers",
          acceptedAnswers: [
            "The biggest number that will divide into both numbers",
            "The largest factor shared by both numbers",
            "The highest factor common to both numbers",
            "The greatest common factor",
            "The largest common factor"
          ],
          hint1: "HCF stands for highest common factor.",
          hint2: "It is the largest number that is a factor of both numbers.",
          hint3: "The HCF is the biggest number that divides into both numbers exactly."
        },
        {
          id: "7.2.5",
          type: "choice",
          marks: 1,
          question: "How many significant figures is 0.405 given to?",
          options: ["One", "Two", "Three", "Four"],
          answer: "Three",
          hint1: "Start counting from the first non-zero digit.",
          hint2: "The significant figures are 4, 0 and 5.",
          hint3: "0.405 has 3 significant figures."
        }
      ],
      level2: [
        {
          id: "7.2.6",
          type: "text",
          marks: 1,
          question: "What is primary data?",
          answer: "Data you have collected yourself",
          acceptedAnswers: [
            "Data you have collected yourself",
            "Data collected yourself",
            "Data gathered first hand",
            "First hand data",
            "Data collected directly by the researcher"
          ],
          hint1: "Primary data is not taken from another source.",
          hint2: "It is collected first hand.",
          hint3: "Primary data is data you have collected yourself."
        },
        {
          id: "7.2.7",
          type: "choice",
          marks: 1,
          question: "The ratio of red to yellow sweets in a bag is 2:5. Red sweets make up...",
          options: ["2/5 of the bag.", "2/7 of the bag.", "5/7 of the bag.", "7/5 of the bag."],
          answer: "2/7 of the bag.",
          hint1: "Add the ratio parts to find the total number of parts.",
          hint2: "2 + 5 = 7 total parts.",
          hint3: "Red sweets are 2 parts out of 7, so red = 2/7."
        },
        {
          id: "7.2.8",
          type: "choice",
          marks: 1,
          question: "A sector of a circle is...",
          options: [
            "...a wedge-shaped area cut from the centre of the circle.",
            "...part of the circumference of a circle.",
            "...a straight line that just touches the outside of the circle.",
            "...the distance from the centre of a circle to its edge."
          ],
          answer: "...a wedge-shaped area cut from the centre of the circle.",
          hint1: "A sector looks like a slice of pizza.",
          hint2: "It is bounded by two radii and an arc.",
          hint3: "A sector is a wedge-shaped area cut from the centre of a circle."
        },
        {
          id: "7.2.9",
          type: "number",
          marks: 1,
          question: "Estimate 102.1 × 3.04.",
          answer: "300",
          acceptedAnswers: ["300"],
          hint1: "Round each number to 1 significant figure.",
          hint2: "102.1 ≈ 100 and 3.04 ≈ 3.",
          hint3: "100 × 3 = 300."
        },
        {
          id: "7.2.10",
          type: "text",
          marks: 2,
          question: "Simplify: 2a + 7 + a² − a − 4",
          answer: "a² + a + 3",
          acceptedAnswers: [
            "a² + a + 3",
            "a^2 + a + 3",
            "a²+a+3",
            "a^2+a+3",
            "a + a² + 3",
            "a+a^2+3"
          ],
          hint1: "Group like terms.",
          hint2: "2a − a = a and 7 − 4 = 3.",
          hint3: "The simplified expression is a² + a + 3."
        }
      ]
    }
  },

  {
    id: "7.3",
    title: "Mixed Practice Test 3",
    slug: "mixed-test-3",
    pageRef: "Pages 162-163",
    difficulty: "intermediate",
    learn: {
      explanation:
        "This mixed test moves into intermediate GCSE skills, including standard form, sets, scale drawings, rounding, congruence and powers.",
      keyPoints: [
        "Covers: coordinates, range, standard form, complements, scale drawings, rounding, decimals, congruence, modal class and powers.",
        "For powers, remember that a power raised to a power means multiply the indices.",
        "For modal class, choose the class interval with the highest frequency."
      ],
    },
    practice: {
      level1: [
        {
          id: "7.3.1",
          type: "text",
          marks: 1,
          question: "A point A is plotted at (4, 3) on a graph. What are its coordinates?",
          answer: "(4, 3)",
          acceptedAnswers: ["(4,3)", "(4, 3)", "4,3", "4, 3"],
          hint1: "Coordinates are written as (x, y).",
          hint2: "x = 4 and y = 3.",
          hint3: "The coordinates are (4, 3)."
        },
        {
          id: "7.3.2",
          type: "number",
          marks: 1,
          question: "What is the range of these values: 1, 4, 5, 9?",
          answer: "8",
          acceptedAnswers: ["8"],
          hint1: "Range = highest value − lowest value.",
          hint2: "9 − 1 = 8.",
          hint3: "The range is 8."
        },
        {
          id: "7.3.3",
          type: "choice",
          marks: 1,
          question: "The number 4136 written in standard form is...",
          options: ["0.4136 × 10⁵", "4.136 × 10³", "0.4136 × 10³", "4.136 × 10⁻³"],
          answer: "4.136 × 10³",
          hint1: "Standard form is A × 10ⁿ, where 1 ≤ A < 10.",
          hint2: "Move the decimal point 3 places left.",
          hint3: "4136 = 4.136 × 10³."
        },
        {
          id: "7.3.4",
          type: "choice",
          marks: 1,
          question: "The complement of set A is...",
          options: [
            "...the group of items that elements of set A are selected from.",
            "...the number of elements in set A.",
            "...the elements of the universal set that are not in set A.",
            "...the sum of the elements in set A."
          ],
          answer: "...the elements of the universal set that are not in set A.",
          hint1: "The complement means everything not in the set.",
          hint2: "The complement of A is written A'.",
          hint3: "A' means the elements of the universal set that are not in A."
        },
        {
          id: "7.3.5",
          type: "choice",
          marks: 1,
          question: "A scale drawing of a pool shows length = 6 cm. The scale is 1 cm = 4 m. What is the real length?",
          options: ["6 m", "600 m", "12 m", "24 m"],
          answer: "24 m",
          hint1: "Each centimetre represents 4 metres.",
          hint2: "6 × 4 = 24.",
          hint3: "The real length is 24 m."
        }
      ],
      level2: [
        {
          id: "7.3.6",
          type: "number",
          marks: 1,
          question: "Round 9.754 to one decimal place.",
          answer: "9.8",
          acceptedAnswers: ["9.8"],
          hint1: "One decimal place means keep the tenths digit.",
          hint2: "Look at the second decimal place: 5.",
          hint3: "9.754 rounds to 9.8."
        },
        {
          id: "7.3.7",
          type: "number",
          marks: 1,
          question: "Write 2/10 as a decimal.",
          answer: "0.2",
          acceptedAnswers: ["0.2", ".2"],
          hint1: "Divide 2 by 10.",
          hint2: "2 ÷ 10 = 0.2.",
          hint3: "2/10 = 0.2."
        },
        {
          id: "7.3.8",
          type: "choice",
          marks: 1,
          question: "Congruent shapes are...",
          options: [
            "...the same shape but can be any size.",
            "...shapes whose angles always add to 180°.",
            "...identical in shape and size.",
            "...shapes with all interior angles equal."
          ],
          answer: "...identical in shape and size.",
          hint1: "Congruent means same shape and same size.",
          hint2: "The shapes may be rotated, reflected or translated.",
          hint3: "Congruent shapes are identical in shape and size."
        },
        {
          id: "7.3.9",
          type: "text",
          marks: 1,
          question: "A frequency table shows: 0-5 has frequency 4, 6-10 has frequency 7, 11-15 has frequency 9, and 16-20 has frequency 4. What is the modal class?",
          answer: "11-15",
          acceptedAnswers: ["11-15", "11 - 15", "11 to 15", "11–15"],
          hint1: "The modal class has the highest frequency.",
          hint2: "The highest frequency is 9.",
          hint3: "The modal class is 11-15."
        },
        {
          id: "7.3.10",
          type: "choice",
          marks: 1,
          question: "Which is a correct simplification of (x³)²?",
          options: ["x⁻¹", "x⁵", "x⁶", "x²³"],
          answer: "x⁶",
          hint1: "When raising a power to a power, multiply the indices.",
          hint2: "3 × 2 = 6.",
          hint3: "(x³)² = x⁶."
        }
      ]
    }
  },

  {
    id: "7.4",
    title: "Mixed Practice Test 4",
    slug: "mixed-test-4",
    pageRef: "Pages 164-165",
    difficulty: "intermediate",
    learn: {
      explanation:
        "This mixed test checks formula recall, ratio, powers, Venn notation, expanding brackets and percentage increase.",
      keyPoints: [
        "Covers: triangle area, improper fractions, probability totals, LCM, quadrilateral angles, ratios, powers, Venn diagrams, expanding brackets and percentage increase.",
        "For percentage increase, use change ÷ original × 100.",
        "For expanding brackets, multiply every term inside the bracket."
      ],
    },
    practice: {
      level1: [
        {
          id: "7.4.1",
          type: "text",
          marks: 1,
          question: "What is the formula for calculating the area of a triangle?",
          answer: "A = 1/2 × base × height",
          acceptedAnswers: [
            "A = 1/2 × base × height",
            "A = 1/2 x base x height",
            "A = 0.5 x base x height",
            "A = base x height / 2",
            "1/2 x base x height",
            "base x height / 2",
            "bh/2",
            "1/2bh",
            "0.5bh"
          ],
          hint1: "A triangle is half of a rectangle with the same base and height.",
          hint2: "Area = half × base × height.",
          hint3: "A = 1/2 × base × height."
        },
        {
          id: "7.4.2",
          type: "choice",
          marks: 1,
          question: "Which of the following is an improper fraction?",
          options: ["2/5", "1/3", "7/4", "3/8"],
          answer: "7/4",
          hint1: "An improper fraction has a numerator greater than its denominator.",
          hint2: "The top number is bigger than the bottom number.",
          hint3: "7/4 is an improper fraction."
        },
        {
          id: "7.4.3",
          type: "number",
          marks: 1,
          question: "What must the probabilities of all possible outcomes of an event add up to?",
          answer: "1",
          acceptedAnswers: ["1", "one"],
          hint1: "All possible outcomes together represent certainty.",
          hint2: "A probability of 1 means certain.",
          hint3: "They add up to 1."
        },
        {
          id: "7.4.4",
          type: "choice",
          marks: 1,
          question: "The lowest common multiple of two numbers is...",
          options: [
            "...the lowest number that will divide into both numbers.",
            "...the highest number that will divide into both numbers.",
            "...the lowest number that both numbers will divide into exactly.",
            "...the value of the two numbers multiplied together."
          ],
          answer: "...the lowest number that both numbers will divide into exactly.",
          hint1: "A multiple is in a times table.",
          hint2: "The LCM is the smallest number in both times tables.",
          hint3: "It is the lowest number that both numbers divide into exactly."
        },
        {
          id: "7.4.5",
          type: "number",
          marks: 1,
          question: "What value do all of the interior angles in a quadrilateral add up to?",
          answer: "360",
          acceptedAnswers: ["360", "360°"],
          hint1: "A quadrilateral has 4 sides.",
          hint2: "A quadrilateral can be split into 2 triangles.",
          hint3: "2 × 180° = 360°."
        }
      ],
      level2: [
        {
          id: "7.4.6",
          type: "text",
          marks: 1,
          question: "Reduce this ratio to its simplest form: 6:18",
          answer: "1:3",
          acceptedAnswers: ["1:3", "1 : 3"],
          hint1: "Divide both parts by their HCF.",
          hint2: "The HCF of 6 and 18 is 6.",
          hint3: "6:18 simplifies to 1:3."
        },
        {
          id: "7.4.7",
          type: "text",
          marks: 1,
          question: "Simplify a² × a⁵ as much as possible.",
          answer: "a⁷",
          acceptedAnswers: ["a⁷", "a^7", "a7"],
          hint1: "When multiplying powers with the same base, add the indices.",
          hint2: "2 + 5 = 7.",
          hint3: "a² × a⁵ = a⁷."
        },
        {
          id: "7.4.8",
          type: "choice",
          marks: 1,
          question: "In a Venn diagram, the shaded overlap area of sets A and B represents...",
          options: ["A", "A'", "A ∩ B", "A' ∪ B"],
          answer: "A ∩ B",
          hint1: "The overlap is the intersection.",
          hint2: "The intersection means elements in both sets.",
          hint3: "The overlap is A ∩ B."
        },
        {
          id: "7.4.9",
          type: "text",
          marks: 1,
          question: "Expand this expression: 5(2x + 5)",
          answer: "10x + 25",
          acceptedAnswers: ["10x + 25", "10x+25"],
          hint1: "Multiply 5 by both terms inside the bracket.",
          hint2: "5 × 2x = 10x and 5 × 5 = 25.",
          hint3: "5(2x + 5) = 10x + 25."
        },
        {
          id: "7.4.10",
          type: "choice",
          marks: 2,
          question: "The price of a sandwich increased from £1.00 to £1.50. What is the percentage increase?",
          options: ["50%", "75%", "100%", "150%"],
          answer: "50%",
          hint1: "Find the change in price first.",
          hint2: "Change = £0.50. Original price = £1.00.",
          hint3: "0.50 ÷ 1.00 × 100 = 50%."
        }
      ]
    }
  },

  {
    id: "7.5",
    title: "Mixed Practice Test 5",
    slug: "mixed-test-5",
    pageRef: "Pages 165-166",
    difficulty: "intermediate",
    learn: {
      explanation:
        "This mixed test covers sequences, volume, symmetry, gradients, irrational numbers, ratio, probability bias, polygon angles, midpoints and roots.",
      keyPoints: [
        "Covers: geometric sequences, cylinder volume, rotational symmetry, gradient formula, irrational numbers, ratio problems, bias, interior angles, midpoints and square roots.",
        "For square root questions, remember that a positive square number has two square roots.",
        "For midpoint questions, average the x-coordinates and average the y-coordinates."
      ],
    },
    practice: {
      level1: [
        {
          id: "7.5.1",
          type: "number",
          marks: 1,
          question: "What is next in the sequence: 5, 10, 20, 40, ...?",
          answer: "80",
          acceptedAnswers: ["80"],
          hint1: "Look at how each term changes.",
          hint2: "Each term doubles.",
          hint3: "40 × 2 = 80."
        },
        {
          id: "7.5.2",
          type: "text",
          marks: 1,
          question: "Write the formula for calculating the volume of a cylinder.",
          answer: "V = πr²h",
          acceptedAnswers: [
            "V = πr²h",
            "V=πr²h",
            "pi r^2 h",
            "πr^2h",
            "π × r² × h",
            "pi x r^2 x h",
            "V = pi x r^2 x h"
          ],
          hint1: "Volume = area of circular cross-section × height.",
          hint2: "The area of a circle is πr².",
          hint3: "V = πr²h."
        },
        {
          id: "7.5.3",
          type: "number",
          marks: 1,
          question: "What is the order of rotational symmetry of a regular pentagon?",
          answer: "5",
          acceptedAnswers: ["5"],
          hint1: "For a regular polygon, the order of rotational symmetry equals the number of sides.",
          hint2: "A pentagon has 5 sides.",
          hint3: "The order of rotational symmetry is 5."
        },
        {
          id: "7.5.4",
          type: "choice",
          marks: 1,
          question: "The formula for calculating the gradient of a straight line is...",
          options: [
            "...change in y ÷ change in x.",
            "...change in x ÷ change in y.",
            "...change in x × change in y.",
            "...change in y − change in x."
          ],
          answer: "...change in y ÷ change in x.",
          hint1: "Gradient is rise over run.",
          hint2: "Rise means change in y. Run means change in x.",
          hint3: "Gradient = change in y ÷ change in x."
        },
        {
          id: "7.5.5",
          type: "text",
          marks: 1,
          question: "What are irrational numbers?",
          answer: "Numbers that cannot be written as fractions",
          acceptedAnswers: [
            "Numbers that cannot be written as fractions",
            "Numbers that cannot be written as a fraction",
            "Numbers that cannot be expressed as fractions",
            "Numbers that cannot be written as a/b",
            "Numbers that are not rational"
          ],
          hint1: "A rational number can be written as a fraction.",
          hint2: "Irrational numbers cannot be written exactly as a fraction.",
          hint3: "Irrational numbers cannot be written as fractions."
        }
      ],
      level2: [
        {
          id: "7.5.6",
          type: "number",
          marks: 2,
          question: "A packet of pens has blue and green pens in the ratio 3:2. There are 12 blue pens. How many green pens are there?",
          answer: "8",
          acceptedAnswers: ["8"],
          hint1: "3 parts represent 12 blue pens.",
          hint2: "1 part = 12 ÷ 3 = 4 pens.",
          hint3: "Green pens = 2 parts = 2 × 4 = 8."
        },
        {
          id: "7.5.7",
          type: "choice",
          marks: 1,
          question: "If the possible results of an event have unequal probabilities, the event is...",
          options: ["...accurate.", "...fair.", "...biased.", "...uncertain."],
          answer: "...biased.",
          hint1: "A fair event has equally likely outcomes.",
          hint2: "Unequal probabilities mean the outcomes are not equally likely.",
          hint3: "The event is biased."
        },
        {
          id: "7.5.8",
          type: "text",
          marks: 1,
          question: "What is the formula for working out the sum of the interior angles of a polygon with n sides?",
          answer: "(n − 2) × 180",
          acceptedAnswers: [
            "(n - 2) x 180",
            "(n − 2) × 180",
            "(n-2)*180",
            "180(n-2)",
            "180 x (n - 2)",
            "180(n − 2)"
          ],
          hint1: "A polygon can be split into triangles.",
          hint2: "An n-sided polygon has n − 2 triangles.",
          hint3: "Sum of interior angles = (n − 2) × 180°."
        },
        {
          id: "7.5.9",
          type: "text",
          marks: 2,
          question: "Point A has coordinates (3, 8). Point B has coordinates (7, 10). Find the midpoint of line AB.",
          answer: "(5, 9)",
          acceptedAnswers: ["(5,9)", "(5, 9)", "5,9", "5, 9"],
          hint1: "Average the x-coordinates and average the y-coordinates.",
          hint2: "x = (3 + 7) ÷ 2 = 5 and y = (8 + 10) ÷ 2 = 9.",
          hint3: "The midpoint is (5, 9)."
        },
        {
          id: "7.5.10",
          type: "text",
          marks: 1,
          question: "Write down both square roots of 81.",
          answer: "9 and -9",
          acceptedAnswers: ["9 and -9", "-9 and 9", "9, -9", "-9, 9", "±9", "+9 and -9", "9 and −9", "−9 and 9"],
          hint1: "9 × 9 = 81.",
          hint2: "(−9) × (−9) = 81.",
          hint3: "The two square roots are 9 and −9."
        }
      ]
    }
  },

  {
    id: "7.6",
    title: "Mixed Practice Test 6",
    slug: "mixed-test-6",
    pageRef: "Pages 166-167",
    difficulty: "intermediate",
    learn: {
      explanation:
        "This mixed test focuses on symmetry, percentages, angle facts, quadratic graphs, proportion, factorising, speed and prime factorisation.",
      keyPoints: [
        "Covers: lines of symmetry, decimal to percentage, co-interior angles, y = x², inverse proportion, factorising, speed, prime factorisation and index laws.",
        "For angle questions, identify the exact angle rule being used.",
        "For prime factorisation, write the answer as a product of prime powers."
      ],
    },
    practice: {
      level1: [
        {
          id: "7.6.1",
          type: "number",
          marks: 1,
          question: "How many lines of symmetry does a regular octagon have?",
          answer: "8",
          acceptedAnswers: ["8"],
          hint1: "A regular polygon has equal sides and equal angles.",
          hint2: "For a regular polygon, the number of lines of symmetry equals the number of sides.",
          hint3: "A regular octagon has 8 lines of symmetry."
        },
        {
          id: "7.6.2",
          type: "text",
          marks: 1,
          question: "What is 0.42 as a percentage?",
          answer: "42%",
          acceptedAnswers: ["42%", "42"],
          hint1: "Multiply the decimal by 100.",
          hint2: "0.42 × 100 = 42.",
          hint3: "0.42 = 42%."
        },
        {
          id: "7.6.3",
          type: "choice",
          marks: 1,
          question: "Two parallel lines are cut by a transversal. One co-interior angle is 120°. Find the other co-interior angle.",
          options: ["60°", "120°", "180°", "240°"],
          answer: "60°",
          hint1: "Co-interior angles add to 180°.",
          hint2: "180° − 120° = 60°.",
          hint3: "The other co-interior angle is 60°."
        },
        {
          id: "7.6.4",
          type: "choice",
          marks: 1,
          question: "Which of these describes the graph of y = x²?",
          options: ["A straight line going up", "A U-shaped curve called a parabola", "A straight line going down", "A circle"],
          answer: "A U-shaped curve called a parabola",
          hint1: "y = x² is a quadratic graph.",
          hint2: "A quadratic graph is curved.",
          hint3: "The graph of y = x² is a U-shaped parabola."
        },
        {
          id: "7.6.5",
          type: "choice",
          marks: 1,
          question: "When variable x increases and variable y decreases in a proportional relationship, x and y are...",
          options: ["...simultaneous.", "...directly proportional.", "...inversely proportional.", "...unrelated."],
          answer: "...inversely proportional.",
          hint1: "Direct proportion means both increase together.",
          hint2: "Inverse proportion means one increases as the other decreases.",
          hint3: "x and y are inversely proportional."
        }
      ],
      level2: [
        {
          id: "7.6.6",
          type: "text",
          marks: 2,
          question: "Factorise 2p² + 4pr.",
          answer: "2p(p + 2r)",
          acceptedAnswers: ["2p(p + 2r)", "2p(p+2r)"],
          hint1: "Find the highest common factor of both terms.",
          hint2: "The HCF is 2p.",
          hint3: "2p² + 4pr = 2p(p + 2r)."
        },
        {
          id: "7.6.7",
          type: "text",
          marks: 2,
          question: "A man walks 30 metres in 20 seconds. Calculate his walking speed. Use speed = distance ÷ time.",
          answer: "1.5 m/s",
          acceptedAnswers: ["1.5", "1.5 m/s", "1.5m/s", "1.5 metres per second", "1.5 meters per second"],
          hint1: "Speed = distance ÷ time.",
          hint2: "30 ÷ 20 = 1.5.",
          hint3: "The walking speed is 1.5 m/s."
        },
        {
          id: "7.6.8",
          type: "text",
          marks: 2,
          question: "Write down the prime factorisation of 112.",
          answer: "2⁴ × 7",
          acceptedAnswers: ["2^4 x 7", "2^4 × 7", "2⁴ × 7", "2⁴x7", "16 x 7", "2*2*2*2*7"],
          hint1: "Keep dividing by prime numbers.",
          hint2: "112 = 2 × 2 × 2 × 2 × 7.",
          hint3: "112 = 2⁴ × 7."
        },
        {
          id: "7.6.9",
          type: "text",
          marks: 1,
          question: "Simplify a² × a¹⁰ × a⁴.",
          answer: "a¹⁶",
          acceptedAnswers: ["a¹⁶", "a^16", "a16"],
          hint1: "When multiplying powers with the same base, add the indices.",
          hint2: "2 + 10 + 4 = 16.",
          hint3: "a² × a¹⁰ × a⁴ = a¹⁶."
        },
        {
          id: "7.6.10",
          type: "text",
          marks: 1,
          question: "Convert 3/8 to a percentage.",
          answer: "37.5%",
          acceptedAnswers: ["37.5%", "37.5"],
          hint1: "Convert the fraction to a decimal first.",
          hint2: "3 ÷ 8 = 0.375.",
          hint3: "0.375 × 100 = 37.5%."
        }
      ]
    }
  },

  {
    id: "7.7",
    title: "Mixed Practice Test 7",
    slug: "mixed-test-7",
    pageRef: "Pages 167-168",
    difficulty: "higher",
    learn: {
      explanation:
        "This higher mixed test includes percentage formulae, surface area, simultaneous equations, direct proportion, relative frequency, translations and expected frequency.",
      keyPoints: [
        "Covers: triangle types, percentages, surface area, simultaneous equations, direct proportion graphs, relative frequency, solving equations, translations and expected frequency.",
        "For translations, add the vector components to the coordinates.",
        "For expected frequency, multiply probability by the number of trials."
      ],
    },
    practice: {
      level1: [
        {
          id: "7.7.1",
          type: "choice",
          marks: 1,
          question: "A scalene triangle...",
          options: [
            "...has three sides of equal length.",
            "...has two sides of equal length.",
            "...has three unequal sides.",
            "...has three equal interior angles."
          ],
          answer: "...has three unequal sides.",
          hint1: "Scalene means no equal sides.",
          hint2: "All three side lengths are different.",
          hint3: "A scalene triangle has three unequal sides."
        },
        {
          id: "7.7.2",
          type: "choice",
          marks: 1,
          question: "Which formula calculates A as a percentage of B?",
          options: ["(A/B) × 100", "(A+B)/B × 100", "(B/A) × 100", "A × B × 100"],
          answer: "(A/B) × 100",
          hint1: "Put the part over the whole.",
          hint2: "A is being compared to B.",
          hint3: "A as a percentage of B = (A/B) × 100."
        },
        {
          id: "7.7.3",
          type: "text",
          marks: 1,
          question: "What is the surface area of a 3D shape?",
          answer: "The total area of all the faces added together",
          acceptedAnswers: [
            "The total area of all the faces added together",
            "The total area of all faces",
            "The sum of the areas of all the faces",
            "All the face areas added together"
          ],
          hint1: "A 3D shape has faces.",
          hint2: "Surface area means add the area of every face.",
          hint3: "Surface area is the total area of all the faces added together."
        },
        {
          id: "7.7.4",
          type: "text",
          marks: 1,
          question: "How can you use graphs of simultaneous equations to find the solution?",
          answer: "Use the coordinates of the point where the graphs cross",
          acceptedAnswers: [
            "Use the coordinates of the point where the graphs cross",
            "The point where the graphs cross",
            "The intersection point",
            "Where the lines intersect",
            "The x and y values where the graphs cross"
          ],
          hint1: "The solution satisfies both equations.",
          hint2: "On a graph, this happens where the lines cross.",
          hint3: "The solution is the coordinates of the point where the graphs cross."
        },
        {
          id: "7.7.5",
          type: "text",
          marks: 1,
          question: "Describe how a graph of x against y will look if x and y are directly proportional.",
          answer: "A straight line through the origin",
          acceptedAnswers: [
            "A straight line through the origin",
            "Straight line through the origin",
            "A line through (0,0)",
            "A straight line through (0, 0)"
          ],
          hint1: "Direct proportion has a constant multiplier.",
          hint2: "The graph is linear and passes through (0, 0).",
          hint3: "It is a straight line through the origin."
        }
      ],
      level2: [
        {
          id: "7.7.6",
          type: "text",
          marks: 1,
          question: "Write the formula for calculating relative frequency.",
          answer: "relative frequency = frequency / number of trials",
          acceptedAnswers: [
            "relative frequency = frequency / number of trials",
            "frequency / number of trials",
            "frequency ÷ number of trials",
            "relative frequency = frequency ÷ trials",
            "number of times it happens / number of trials"
          ],
          hint1: "Relative frequency uses experimental results.",
          hint2: "Divide how often it happened by the total number of trials.",
          hint3: "Relative frequency = frequency ÷ number of trials."
        },
        {
          id: "7.7.7",
          type: "number",
          marks: 2,
          question: "If 3x + 4 = 25, what is the value of x?",
          answer: "7",
          acceptedAnswers: ["7"],
          hint1: "Subtract 4 from both sides.",
          hint2: "3x = 21.",
          hint3: "x = 21 ÷ 3 = 7."
        },
        {
          id: "7.7.8",
          type: "choice",
          marks: 1,
          question: "A triangle has point A at (1, 1). It is translated by vector (4, 7). What are the new coordinates of point A?",
          options: ["(4, 7)", "(5, 8)", "(−3, −6)", "(7, 14)"],
          answer: "(5, 8)",
          hint1: "Add the vector to the coordinate.",
          hint2: "x: 1 + 4 = 5. y: 1 + 7 = 8.",
          hint3: "The new coordinates are (5, 8)."
        },
        {
          id: "7.7.9",
          type: "number",
          marks: 1,
          question: "P(landing on C) = 0.4. Estimate how many times the spinner lands on C if spun 20 times.",
          answer: "8",
          acceptedAnswers: ["8"],
          hint1: "Expected frequency = probability × number of trials.",
          hint2: "0.4 × 20 = 8.",
          hint3: "The spinner is expected to land on C 8 times."
        },
        {
          id: "7.7.10",
          type: "text",
          marks: 1,
          question: "Simplify (x²)⁵.",
          answer: "x¹⁰",
          acceptedAnswers: ["x¹⁰", "x^10", "x10"],
          hint1: "When raising a power to a power, multiply the indices.",
          hint2: "2 × 5 = 10.",
          hint3: "(x²)⁵ = x¹⁰."
        }
      ]
    }
  },

  {
    id: "7.8",
    title: "Mixed Practice Test 8",
    slug: "mixed-test-8",
    pageRef: "Pages 168-169",
    difficulty: "higher",
    learn: {
      explanation:
        "This higher mixed test checks rounding, qualitative data, area formulae, correlation, set notation, percentage change, time conversion, sequences and double brackets.",
      keyPoints: [
        "Covers: rounding, qualitative data, parallelogram area, correlation, union of sets, percentage change, time conversion, sequence membership and expanding double brackets.",
        "For sequence membership, solve for n and check whether n is a positive whole number.",
        "For double brackets, multiply each term in the first bracket by each term in the second bracket."
      ],
    },
    practice: {
      level1: [
        {
          id: "7.8.1",
          type: "number",
          marks: 1,
          question: "Round 827 682 to the nearest thousand.",
          answer: "828000",
          acceptedAnswers: ["828000", "828,000", "828 000"],
          hint1: "Nearest thousand means look at the hundreds digit.",
          hint2: "The hundreds digit is 6, so round up.",
          hint3: "827 682 rounds to 828 000."
        },
        {
          id: "7.8.2",
          type: "text",
          marks: 1,
          question: "What is qualitative data?",
          answer: "Data that is descriptive and does not use numbers",
          acceptedAnswers: [
            "Data that is descriptive and does not use numbers",
            "Descriptive data",
            "Data in words",
            "Non-numerical data",
            "Data that describes qualities"
          ],
          hint1: "Qualitative data is not numerical.",
          hint2: "It often uses words or categories.",
          hint3: "Qualitative data is descriptive data that does not use numbers."
        },
        {
          id: "7.8.3",
          type: "text",
          marks: 1,
          question: "Write the formula for the area of a parallelogram.",
          answer: "A = base × height",
          acceptedAnswers: [
            "A = base × height",
            "A = base x height",
            "base x height",
            "base × height",
            "bh",
            "b x h",
            "b × h"
          ],
          hint1: "The formula is similar to the area of a rectangle.",
          hint2: "Use the perpendicular height.",
          hint3: "Area of a parallelogram = base × height."
        },
        {
          id: "7.8.4",
          type: "choice",
          marks: 1,
          question: "A scatter graph has points making a clear straight line. As one variable increases, the other also increases. This shows...",
          options: [
            "...weak negative correlation.",
            "...strong negative correlation.",
            "...weak positive correlation.",
            "...strong positive correlation."
          ],
          answer: "...strong positive correlation.",
          hint1: "Both variables increase, so the correlation is positive.",
          hint2: "The points form a clear line, so the correlation is strong.",
          hint3: "This shows strong positive correlation."
        },
        {
          id: "7.8.5",
          type: "choice",
          marks: 1,
          question: "The union of two sets...",
          options: [
            "...contains only the elements in both sets.",
            "...contains all elements in either set.",
            "...is the group of items elements are selected from.",
            "...is all elements from the universal set in neither set."
          ],
          answer: "...contains all elements in either set.",
          hint1: "Union means A or B or both.",
          hint2: "The symbol is ∪.",
          hint3: "The union contains all elements in either set."
        }
      ],
      level2: [
        {
          id: "7.8.6",
          type: "text",
          marks: 1,
          question: "Write the formula for percentage change.",
          answer: "percentage change = (change / original) × 100",
          acceptedAnswers: [
            "percentage change = (change / original) x 100",
            "percentage change = (change ÷ original) × 100",
            "(change / original) x 100",
            "(change ÷ original) × 100",
            "change/original x 100",
            "change divided by original times 100"
          ],
          hint1: "Find the change first.",
          hint2: "Divide the change by the original amount.",
          hint3: "Percentage change = change ÷ original × 100."
        },
        {
          id: "7.8.7",
          type: "number",
          marks: 1,
          question: "Convert 3 hours 20 minutes into minutes.",
          answer: "200",
          acceptedAnswers: ["200", "200 minutes"],
          hint1: "1 hour = 60 minutes.",
          hint2: "3 hours = 180 minutes.",
          hint3: "180 + 20 = 200 minutes."
        },
        {
          id: "7.8.8",
          type: "text",
          marks: 2,
          question: "A sequence is given by the rule 2n + 1. Is 18 part of the sequence? Explain briefly.",
          answer: "No, because n would be 8.5, which is not a whole number",
          acceptedAnswers: [
            "No",
            "No, n = 8.5",
            "No because n = 8.5",
            "No, because n would be 8.5",
            "No, because 2n + 1 = 18 gives n = 8.5",
            "No because n is not a whole number"
          ],
          hint1: "Set 2n + 1 equal to 18.",
          hint2: "2n + 1 = 18 gives 2n = 17.",
          hint3: "n = 8.5, so 18 is not part of the sequence."
        },
        {
          id: "7.8.9",
          type: "choice",
          marks: 1,
          question: "Which is the correct expansion of (x + 2)(x − 2)?",
          options: ["2x", "x² − 4", "x² + 4", "x² + 4x − 4"],
          answer: "x² − 4",
          hint1: "This is a difference of two squares.",
          hint2: "x² − 2x + 2x − 4.",
          hint3: "(x + 2)(x − 2) = x² − 4."
        },
        {
          id: "7.8.10",
          type: "text",
          marks: 2,
          question: "Expand and simplify (x + 3)(x + 5).",
          answer: "x² + 8x + 15",
          acceptedAnswers: [
            "x² + 8x + 15",
            "x^2 + 8x + 15",
            "x²+8x+15",
            "x^2+8x+15"
          ],
          hint1: "Multiply every term in the first bracket by every term in the second bracket.",
          hint2: "x² + 5x + 3x + 15.",
          hint3: "x² + 8x + 15."
        }
      ]
    }
  },

  {
    id: "7.9",
    title: "Practice Paper 1 Style Questions (Non-Calculator)",
    slug: "practice-paper-1",
    pageRef: "Pages 170-179",
    difficulty: "higher",
    learn: {
      explanation:
        "A non-calculator set of practice paper-style questions. Work carefully and show method where possible. These questions are designed to build exam fluency rather than replace a full official paper.",
      keyPoints: [
        "No calculator allowed.",
        "Show all working — method marks matter.",
        "Check that your answers are reasonable.",
        "Formulae used in this section include Pythagoras' theorem and common area/volume formulae."
      ],
    },
    practice: {
      level1: [
        {
          id: "7.9.1",
          type: "order",
          marks: 2,
          question: "Write these in order of size, starting with the smallest: 0.4, 1/4, 0.15, 0.05",
          answer: "0.05, 0.15, 1/4, 0.4",
          acceptedAnswers: ["0.05, 0.15, 1/4, 0.4", "0.05,0.15,1/4,0.4", "0.05, 0.15, 0.25, 0.4"],
          hint1: "Convert 1/4 to a decimal.",
          hint2: "1/4 = 0.25.",
          hint3: "The order is 0.05, 0.15, 1/4, 0.4."
        },
        {
          id: "7.9.2",
          type: "number",
          marks: 1,
          question: "A bar chart shows the number of 12-year-olds with pets: 0 pets = 3, 1 pet = 5, 2 pets = 4. How many 12-year-olds had fewer than 3 pets?",
          answer: "12",
          acceptedAnswers: ["12"],
          hint1: "Fewer than 3 pets means 0, 1 or 2 pets.",
          hint2: "Add the frequencies: 3 + 5 + 4.",
          hint3: "3 + 5 + 4 = 12."
        },
        {
          id: "7.9.3",
          type: "text",
          marks: 1,
          question: "Find the next two terms: 1, 6, 11, 16, ...",
          answer: "21, 26",
          acceptedAnswers: ["21, 26", "21,26", "21 and 26"],
          hint1: "Find the common difference.",
          hint2: "The sequence increases by 5 each time.",
          hint3: "The next two terms are 21 and 26."
        },
        {
          id: "7.9.4",
          type: "text",
          marks: 1,
          question: "Find the next two terms: 9, 16, 25, 36, ...",
          answer: "49, 64",
          acceptedAnswers: ["49, 64", "49,64", "49 and 64"],
          hint1: "These are square numbers.",
          hint2: "9 = 3², 16 = 4², 25 = 5², 36 = 6².",
          hint3: "The next two terms are 7² = 49 and 8² = 64."
        },
        {
          id: "7.9.5",
          type: "number",
          marks: 1,
          question: "A sequence is given by the rule 4n + 8. Find the 9th term.",
          answer: "44",
          acceptedAnswers: ["44"],
          hint1: "Substitute n = 9.",
          hint2: "4 × 9 + 8 = 36 + 8.",
          hint3: "The 9th term is 44."
        }
      ],
      level2: [
        {
          id: "7.9.6",
          type: "text",
          marks: 2,
          question: "Pat rolls two fair dice and adds the scores. What is the probability of scoring a total of 10?",
          answer: "1/12",
          acceptedAnswers: ["1/12", "3/36", "0.0833", "0.083", "8.33%"],
          hint1: "Possible totals of 10 are (4,6), (5,5), and (6,4).",
          hint2: "There are 3 successful outcomes out of 36 equally likely outcomes.",
          hint3: "The probability is 3/36 = 1/12."
        },
        {
          id: "7.9.7",
          type: "text",
          marks: 1,
          question: "If n is an integer, list all values that satisfy 3 < n < 9.",
          answer: "4, 5, 6, 7, 8",
          acceptedAnswers: ["4, 5, 6, 7, 8", "4,5,6,7,8"],
          hint1: "n must be greater than 3.",
          hint2: "n must be less than 9.",
          hint3: "n = 4, 5, 6, 7, 8."
        },
        {
          id: "7.9.8",
          type: "text",
          marks: 1,
          question: "If n is an integer, list all values that satisfy −1 ≤ n ≤ 5.",
          answer: "−1, 0, 1, 2, 3, 4, 5",
          acceptedAnswers: ["-1, 0, 1, 2, 3, 4, 5", "-1,0,1,2,3,4,5", "−1, 0, 1, 2, 3, 4, 5"],
          hint1: "The inequality includes −1.",
          hint2: "The inequality includes 5.",
          hint3: "n = −1, 0, 1, 2, 3, 4, 5."
        },
        {
          id: "7.9.9",
          type: "number",
          marks: 1,
          question: "A fridge magnet costs £1.65. A customer pays with a £5 note. How much change should they receive?",
          answer: "3.35",
          acceptedAnswers: ["3.35", "£3.35"],
          hint1: "Calculate £5.00 − £1.65.",
          hint2: "500p − 165p = 335p.",
          hint3: "The change is £3.35."
        },
        {
          id: "7.9.10",
          type: "number",
          marks: 2,
          question: "How much does it cost to buy 2 bookmarks at £1.45 each, 2 mugs at £2.90 each and a postcard at £0.60?",
          answer: "9.30",
          acceptedAnswers: ["9.30", "9.3", "£9.30", "£9.3"],
          hint1: "Find the cost of each group of items.",
          hint2: "2 × £1.45 = £2.90 and 2 × £2.90 = £5.80.",
          hint3: "£2.90 + £5.80 + £0.60 = £9.30."
        }
      ],
      level3: [
        {
          id: "7.9.11",
          type: "number",
          marks: 2,
          question: "Find x in a right-angled triangle where the two shorter sides are 3 cm and 4 cm, and the hypotenuse is x.",
          answer: "5",
          acceptedAnswers: ["5", "5cm", "5 cm"],
          hint1: "Use Pythagoras' theorem.",
          hint2: "x² = 3² + 4² = 9 + 16 = 25.",
          hint3: "x = 5 cm."
        },
        {
          id: "7.9.12",
          type: "number",
          marks: 2,
          question: "Find x in a right-angled triangle where the hypotenuse is 10 cm and one shorter side is 8 cm.",
          answer: "6",
          acceptedAnswers: ["6", "6cm", "6 cm"],
          hint1: "Use Pythagoras' theorem and subtract because the hypotenuse is given.",
          hint2: "x² = 10² − 8² = 100 − 64 = 36.",
          hint3: "x = 6 cm."
        },
        {
          id: "7.9.13",
          type: "text",
          marks: 2,
          question: "Expand and simplify: 4(3x + 2) − 6(x − 5).",
          answer: "6x + 38",
          acceptedAnswers: ["6x + 38", "6x+38"],
          hint1: "Expand both brackets carefully.",
          hint2: "4(3x + 2) = 12x + 8 and −6(x − 5) = −6x + 30.",
          hint3: "12x + 8 − 6x + 30 = 6x + 38."
        },
        {
          id: "7.9.14",
          type: "number",
          marks: 2,
          question: "At a concert, the ratio of parents to children is 3:5. There are 120 people in total. How many children are there?",
          answer: "75",
          acceptedAnswers: ["75"],
          hint1: "Add the ratio parts.",
          hint2: "3 + 5 = 8 parts. 120 ÷ 8 = 15 per part.",
          hint3: "Children = 5 × 15 = 75."
        },
        {
          id: "7.9.15",
          type: "number",
          marks: 1,
          question: "The mean of 8 values is 7. What is the sum of the 8 values?",
          answer: "56",
          acceptedAnswers: ["56"],
          hint1: "Mean = sum ÷ number of values.",
          hint2: "7 = sum ÷ 8.",
          hint3: "Sum = 7 × 8 = 56."
        },
        {
          id: "7.9.16",
          type: "number",
          marks: 1,
          question: "Round 84 293 to 3 significant figures.",
          answer: "84300",
          acceptedAnswers: ["84300", "84,300", "84 300"],
          hint1: "Keep the first 3 significant figures: 8, 4 and 2.",
          hint2: "The next digit is 9, so round up.",
          hint3: "84 293 rounds to 84 300."
        },
        {
          id: "7.9.17",
          type: "number",
          marks: 1,
          question: "17 400 people attended a fair, rounded to the nearest 100. What is the minimum possible number of people?",
          answer: "17350",
          acceptedAnswers: ["17350", "17,350", "17 350"],
          hint1: "Rounding to the nearest 100 gives a lower bound halfway below 17 400.",
          hint2: "Half of 100 is 50.",
          hint3: "The minimum possible number is 17 350."
        },
        {
          id: "7.9.18",
          type: "number",
          marks: 1,
          question: "17 400 people attended a fair, rounded to the nearest 100. What is the maximum possible whole number of people?",
          answer: "17449",
          acceptedAnswers: ["17449", "17,449", "17 449"],
          hint1: "The upper boundary is just below 17 450.",
          hint2: "For a whole number, the greatest value is 17 449.",
          hint3: "The maximum possible whole number is 17 449."
        }
      ]
    }
  },

  {
    id: "7.10",
    title: "Practice Paper 2 Style Questions (Calculator)",
    slug: "practice-paper-2",
    pageRef: "Pages 180-189",
    difficulty: "higher",
    learn: {
      explanation:
        "A calculator-allowed set of practice paper-style questions. Use your calculator efficiently, but still show working for method marks.",
      keyPoints: [
        "Calculator allowed — but working still matters.",
        "Check units carefully.",
        "Use exact formulae before rounding.",
        "For reverse percentages, divide by the remaining percentage as a decimal or use the unitary method."
      ],
    },
    practice: {
      level1: [
        {
          id: "7.10.1",
          type: "number",
          marks: 1,
          question: "Convert 750 millimetres to centimetres.",
          answer: "75",
          acceptedAnswers: ["75", "75 cm", "75cm"],
          hint1: "1 cm = 10 mm.",
          hint2: "750 ÷ 10 = 75.",
          hint3: "750 mm = 75 cm."
        },
        {
          id: "7.10.2",
          type: "number",
          marks: 1,
          question: "Convert 750 millimetres to metres.",
          answer: "0.75",
          acceptedAnswers: ["0.75", ".75", "0.75 m", "0.75m"],
          hint1: "1 m = 1000 mm.",
          hint2: "750 ÷ 1000 = 0.75.",
          hint3: "750 mm = 0.75 m."
        },
        {
          id: "7.10.3",
          type: "number",
          marks: 1,
          question: "Convert 31 litres to cm³.",
          answer: "31000",
          acceptedAnswers: ["31000", "31,000", "31 000", "31000 cm3", "31000 cm³"],
          hint1: "1 litre = 1000 cm³.",
          hint2: "31 × 1000 = 31 000.",
          hint3: "31 litres = 31 000 cm³."
        },
        {
          id: "7.10.4",
          type: "number",
          marks: 1,
          question: "A pie chart shows Rugby at 80°, Hockey at 140° and Football at 90°. What angle is Basketball?",
          answer: "50",
          acceptedAnswers: ["50", "50°"],
          hint1: "Angles in a pie chart add to 360°.",
          hint2: "80 + 140 + 90 = 310.",
          hint3: "360 − 310 = 50°."
        },
        {
          id: "7.10.5",
          type: "number",
          marks: 2,
          question: "84 members play hockey. This is represented by a 140° sector on a pie chart. What is the total number of leisure club members?",
          answer: "216",
          acceptedAnswers: ["216"],
          hint1: "140° represents 84 members.",
          hint2: "1° represents 84 ÷ 140 = 0.6 members.",
          hint3: "360° represents 360 × 0.6 = 216 members."
        }
      ],
      level2: [
        {
          id: "7.10.6",
          type: "number",
          marks: 2,
          question: "Solve: 6x + 9 = 57.",
          answer: "8",
          acceptedAnswers: ["8"],
          hint1: "Subtract 9 from both sides.",
          hint2: "6x = 48.",
          hint3: "x = 8."
        },
        {
          id: "7.10.7",
          type: "number",
          marks: 2,
          question: "Solve: 3(3x − 6) = 54.",
          answer: "8",
          acceptedAnswers: ["8"],
          hint1: "Expand the bracket first.",
          hint2: "9x − 18 = 54, so 9x = 72.",
          hint3: "x = 8."
        },
        {
          id: "7.10.8",
          type: "number",
          marks: 2,
          question: "Solve: 4x − 7 = 5x − 2.",
          answer: "-5",
          acceptedAnswers: ["-5", "−5"],
          hint1: "Collect x terms on one side and number terms on the other.",
          hint2: "−7 + 2 = 5x − 4x.",
          hint3: "−5 = x, so x = −5."
        },
        {
          id: "7.10.9",
          type: "text",
          marks: 1,
          question: "Express 40/64 in its simplest form.",
          answer: "5/8",
          acceptedAnswers: ["5/8", "0.625"],
          hint1: "Find the HCF of 40 and 64.",
          hint2: "The HCF is 8.",
          hint3: "40/64 = 5/8."
        },
        {
          id: "7.10.10",
          type: "text",
          marks: 1,
          question: "Simplify: 8 − 2s + 9s − 3.",
          answer: "7s + 5",
          acceptedAnswers: ["7s + 5", "7s+5", "5 + 7s", "5+7s"],
          hint1: "Collect like terms.",
          hint2: "−2s + 9s = 7s and 8 − 3 = 5.",
          hint3: "The simplified expression is 7s + 5."
        }
      ],
      level3: [
        {
          id: "7.10.11",
          type: "number",
          marks: 1,
          question: "A regular polygon has 15 sides. What is each exterior angle?",
          answer: "24",
          acceptedAnswers: ["24", "24°"],
          hint1: "Exterior angles of a polygon add to 360°.",
          hint2: "360 ÷ 15 = 24.",
          hint3: "Each exterior angle is 24°."
        },
        {
          id: "7.10.12",
          type: "number",
          marks: 1,
          question: "A regular polygon has an exterior angle of 40°. How many sides does it have?",
          answer: "9",
          acceptedAnswers: ["9"],
          hint1: "Exterior angles add to 360°.",
          hint2: "Number of sides = 360 ÷ exterior angle.",
          hint3: "360 ÷ 40 = 9 sides."
        },
        {
          id: "7.10.13",
          type: "number",
          marks: 2,
          question: "A circle has diameter 1.8 m. Find its circumference to 2 decimal places.",
          answer: "5.65",
          acceptedAnswers: ["5.65", "5.65 m", "5.65m"],
          hint1: "Use C = πd.",
          hint2: "C = π × 1.8.",
          hint3: "C = 5.65 m to 2 decimal places."
        },
        {
          id: "7.10.14",
          type: "number",
          marks: 2,
          question: "C = 34 + 11n. Find n when C = 210.",
          answer: "16",
          acceptedAnswers: ["16"],
          hint1: "Substitute C = 210.",
          hint2: "210 = 34 + 11n, so 176 = 11n.",
          hint3: "n = 16."
        },
        {
          id: "7.10.15",
          type: "number",
          marks: 2,
          question: "55 people typed words for 1 minute. The total of fx is 2365. Estimate the mean typing speed.",
          answer: "43",
          acceptedAnswers: ["43", "43 words per minute", "43 wpm"],
          hint1: "Estimated mean = total fx ÷ total frequency.",
          hint2: "2365 ÷ 55 = 43.",
          hint3: "The estimated mean is 43 words per minute."
        },
        {
          id: "7.10.16",
          type: "text",
          marks: 2,
          question: "Calculate 2.45 × 10⁷ + 8.33 × 10⁶. Give your answer in standard form.",
          answer: "3.283 × 10⁷",
          acceptedAnswers: [
            "3.283 × 10⁷",
            "3.283 x 10^7",
            "3.283 × 10^7",
            "3.283x10^7",
            "32830000",
            "32,830,000",
            "32 830 000"
          ],
          hint1: "Convert both numbers to ordinary numbers or align powers of 10.",
          hint2: "24 500 000 + 8 330 000 = 32 830 000.",
          hint3: "32 830 000 = 3.283 × 10⁷."
        },
        {
          id: "7.10.17",
          type: "number",
          marks: 3,
          question: "A washing machine is reduced by 13%. The sale price is £348. What was the original price?",
          answer: "400",
          acceptedAnswers: ["400", "£400"],
          hint1: "After a 13% reduction, the sale price is 87% of the original.",
          hint2: "87% = £348, so 1% = £348 ÷ 87 = £4.",
          hint3: "100% = £400."
        },
        {
          id: "7.10.18",
          type: "number",
          marks: 2,
          question: "Find AB using Pythagoras. A right-angled triangle has shorter sides 7 cm and 24 cm. AB is the hypotenuse.",
          answer: "25",
          acceptedAnswers: ["25", "25cm", "25 cm"],
          hint1: "Use Pythagoras' theorem.",
          hint2: "AB² = 7² + 24² = 49 + 576 = 625.",
          hint3: "AB = √625 = 25 cm."
        }
      ]
    }
  }
];

export default examPracticeTopics;